import { c } from "vite/dist/node/types.d-aGj9QkWt";
import { Competition, GetResultsDto, Participant, Result, Discipline, Startgroup, Gender, ResultType, StartnumberRfidTag, ResultList, ParticipantDto, ResultDto } from "./Dtos";
import { AxiosRequestHeaders } from "axios";


/***  
 * What is this file used for:
 * 
 * This file is used for faking responses to requests done by axios instead of actually sending the https requests to the backend. This
 * is useful when developing the frontend without having to bother with the backend impelentation or about data in the database.
 */

export const mockAxiosResponsesForLocalDevelopment = false;


export function mockAxiosResponse(
  method: string,
  url: string,
  requestBody: any,
  requestHeaders: AxiosRequestHeaders
): HandlerOutput {
  let handlerName : string | undefined = undefined;
  Object.keys(requestHandlers).forEach((hname) => {
    var re = new RegExp(hname, "i");
    if (`${method} ${url}`.match(hname)) {
      handlerName = hname;
    }
  })

  const handler = requestHandlers[handlerName ?? ""] ?? defaultHandler;
  return handler({requestBody, requestHeaders, method, url});
}


const requestHandlers : Record<string, (input: HandlerInput) => HandlerOutput> = {
  "GET /api/login": (input: HandlerInput) => { 
    return handleLoginRequest(input.requestHeaders);
  },
  "GET /api/competitions": (input: HandlerInput) => {
    return handleGetAllCompetitionsRequest();
  },
  "GET /api/competition/[^/]": (input: HandlerInput) => {
    const match = input.url.match(/.*competition\/(.+)/);
    return handleGetCompetitionIdRequest(parseInt(match && match[1] || ""));
  },
  "GET /api/competition/[^/]+/participants": (input: HandlerInput) => {
    return handleGetAllParticipants();
  },
  "GET /api/discipline/competitionbytag/[^/]+" : (input: HandlerInput) => {
    const match = input.url.match(/.*competitionbytag\/(.+)/);
    return handleGetDisciplinesByCompetitionTag((match && match[1]) || "");
  },
  "GET /api/competition/([^/]+)/startgroups" : (input: HandlerInput) => {
    const match = input.url.match(/.*competition\/([^/]+)\/.*/);
    return handleGetStartgroupsByCompetitionId(parseInt((match && match[1]) || ""))
  },
  "GET /api/discipline/([^/]+)/startgroups" : (input: HandlerInput) => {
    const match = input.url.match(/.*discipline\/([^/]+)\/.*/);
    return handleGetStartgroupsByDisciplineId(parseInt((match && match[1]) || ""))
  },
  "GET /api/competition/([^/]+)/results" : (input: HandlerInput) => {
    const match = input.url.match(/.*competition\/([^/]+)\/results/);
    return handleGetAllResultsByCompetitionTag((match && match[1]) || "")
  },
  "GET /api/startnumber_rfid_tags/competitionbytag/([^/]+)" : (input: HandlerInput) => {
    const match = input.url.match(/.*competitionbytag\/([^/]+)/);
    return handleGetRfidTagsByCompetitionTag((match && match[1]) || "")
  },
  "GET /api/competition/([^/]+)/disciplines" : (input: HandlerInput) => {
    const match = input.url.match(/.*competition\/([^/]+)\/disciplines/);
    return handleGetDisciplinesByCompetitionId(parseInt((match && match[1]) || "0"))
  },
  "PUT /api/startgroup/([^/]+)" : (input: HandlerInput) => {
    return {status: 200, data: null};
  }
}


interface HandlerInput {
  requestBody: any,
  requestHeaders: AxiosRequestHeaders
  method: string,
  url: string
}

interface HandlerOutput {
  status: number,
  data: any
}


// Mocked response data
const firstnames = ["Glen", "Bosse", "Fredrik", "Anthon", "Fabian", "Jonathan", "Kennet", "Hans-Olof", "Jörgen", "Josefin", "Sofia", "Emil", "Caroline", "Marcus", "Alice", "Liam", "Emma", "Lucas", "Olivia", "Noah"];
const lastnames = ["Johansson", "Alfredsson", "Andersson", "Nilsson Fransson", "Olofsson", "Fransson", "Karlson", "Classon", "Heden", "Pettersson", "Larsson", "Svensson", "Eriksson", "Berg", "Lindgren", "Lundqvist", "Malmberg", "Petson", "Myrgren", "Neldemo", "Genström", "Perk", "Släke", "Kränku" ];
const clubs = ["Slaghökarna 2", "Gimo AK", "Östhammar FI", "Malmö IF", "Öregrungsskidor", "Norrköping Club", "Växjö Team", "Stockholm United", "Lund Skidklubb", "Göteborgs IF", "IFK Roden", "GFMP", "Breks AB"];
const classes = ["M", "K", "JL", "P9", "P11", "P13", "P17", "F9", "F11", "F13", "F17", "H", "HL"];
const startgroups: Array<Startgroup> = [0, 10, 20, 30, 40, 50].map((minutes, id) => {
  const date = new Date(); 
  date.setHours(10);
  date.setMinutes(minutes); 
  return {starttime_planned: date.getTime(), closetime_planned: date.getTime() + 20000, closetime_actual: null, starttime_actual: null, id: id};
});
const competitions : Array<Competition> = [1,2,3,4,5].map((nr) => createCompetition(nr));


function defaultHandler(input: HandlerInput): HandlerOutput {
  // Endpoint not implemented response
  return {status: 501, data: {...input}};
}

function handleLoginRequest(requestHeaders: AxiosRequestHeaders): HandlerOutput {
    const [usr, pwd] = extractUsernameAndPassword(requestHeaders);
    if (usr?.length && pwd?.length) {
      return {status: 200, data: "ADMIN"};
    }
    else {
      return {status: 403, data: undefined};
    }
}

function handleGetAllCompetitionsRequest() {
  return {status: 200, data: competitions.map(c => ({id: c.id, tag: c.tag}))};
}

function handleGetCompetitionIdRequest(competitionId: number) {
  return {status: 200, data: competitions.filter(c => c.id === competitionId).map(c => ({id: c.id, tag: c.tag}))[0]};
}

function handleGetAllParticipants() {
  return {status: 200, data: generateParticipants(666)};
}

function handleGetDisciplinesByCompetitionTag(tag: string) : HandlerOutput {
  return {status: 200, data: competitions.filter(c => c.tag === tag)[0].disciplines};
}

function handleGetAllResultsByCompetitionTag(tag: string) : HandlerOutput {
  return {status: 200, data: [
    {results: generateResults(tag, 666).slice(0, 333), competitionTag: tag, displayOrder: 1, name: "Result list 1", showResults: true},
    {results: generateResults(tag, 666).slice(333, 666), competitionTag: tag, displayOrder: 2, name: "Result list 2", showResults: true},
  ] as Array<ResultList<ResultDto>>};
}

function handleGetRfidTagsByCompetitionTag(tag: string) : HandlerOutput {
  return {status: 200, data: getRfidTags(tag)};
}

function handleGetDisciplinesByCompetitionId(competitionId: number) {
  return {status: 200, data: competitions.filter(c => c.id === competitionId)[0].disciplines};
}

function handleGetStartgroupsByCompetitionId(competitionId: number) {
  return {status: 200, data: competitions.filter(c => c.id === competitionId)[0].startgroups};
}

function handleGetStartgroupsByDisciplineId(disciplineId: number) {
  return {status: 200, data: competitions
    .flatMap(c => c.disciplines)
    .filter(d => d.id === disciplineId)[0].startgroups
  };
}



/*
TODO: implement the following:

export const PUT = {
    participant: async (participant: Participant, competitionid: Competitionid) => {
      console.info('PUT.participant: ' + participant.id + ', competition_id: ' + competitionid.toString());
      var rtn = await axios.put("api/discipline_signedup/update/" + participant.id.toString() + "/participantDTO/competitionid/" + competitionid.toString(), participant);
      console.info('rtn: ' + rtn);
      return true},
    result: async (result: Result) => {
      console.log(`Time: ${result.result} -- ${timeStringToMs(result.result)}`);
      return true
    }
}

export const DELETE = {
    participant: async (participant: Participant) => {
      console.info('DELETE.participant: ' + participant.id);
      var rtn = await axios.delete("api/discipline_signedup/" + participant.id.toString()); 
      return true;}
}

export const POST = {
  participant: async (participant: Participant, competitionid: Competitionid) => {
    console.info('POST.participant: ' + participant.id + ', Namn: ' + participant.firstname + ' ' + participant.lastname + ', competition_id: ' + competitionid.toString());
    var rtn = await axios.post("api/discipline_signedup/add/participantDTO/competitionid/" + competitionid.toString(), participant);
    console.info('rtn: ' + rtn);
    return true},
  result: async (result: Result) => {
    console.log(`Time: ${result.result} -- ${timeStringToMs(result.result)}`);
    return true
  }
}
*/



function extractUsernameAndPassword(headers: AxiosRequestHeaders) {
  // Extract Authorization header
  const authHeader = headers.Authorization || headers.authorization;

  let username, password;
  if (authHeader?.startsWith('Basic ')) {
    const base64Credentials = authHeader.slice('Basic '.length).trim();
    const decodedCredentials = atob(base64Credentials);
    [username, password] = decodedCredentials.split(':');
  }
  return [username, password];
}

function getRfidTags(competitionTag: string) {
  return [1,2,3,4,5,6,7,8,9].map(id => ({id: id, rfidtag: "tag" + id, startnumber: 10 + id, competition_id: 1, competition_tag: competitionTag}));
}

function createCompetition(id: number) : Competition {
  return {
    id: id, 
    //name: `Stadsloppet 202${id}`, 
    tag: `stadsloppet-202${id}`, 
    disciplines: classes.map((name, index) => createDiscipline(index, name)), 
    startgroups: [...classes.map((name, index) => createStartgroup(index)), createStartgroup(classes.length), createStartgroup(classes.length + 1)], // The last one is not assigned to any discipline which is a valid case!
    //deleted: false, 
    //description: `The ${id}:th competition...`, 
    startTime: 1337,
    endTime: 1789,
  };
}

function createDiscipline(id: number, name: string) : Discipline {
  return {
    id: id,
    name: name,
    displayOrder: id + 1,
    groupSize: 300,
    //description: `Discipline${id} description...`,
    startgroups: [createStartgroup(id), createStartgroup(id + 1)],
    resultType: ResultType.INDIVIDUAL, //TODO change to enum for different time types...
    maxAge: null,
    minAge: null,
    distanceInMeters: 6300,
    startNumberSequences: [{firstStartNumber: 1, lastStartNumber: 496}]
    //deleted: false,
  } as Discipline;
}

function createStartgroup(id: number) : Startgroup {
  const time = new Date(Date.now());
  time.setSeconds(0);
  time.setMilliseconds(0);
  const tenMinInMs = 10 * 60 * 1000;
  const thirtyMinInMs = 30 * 60 * 1000;
  const start = time.getTime() + tenMinInMs * id - (id % 2 == 0 ? 0 : tenMinInMs * 2);
  const end = time.getTime() + tenMinInMs * id + thirtyMinInMs;

  return {
    closetime_actual: null,
    closetime_planned: end,
    starttime_actual: null,
    starttime_planned: start,
    id: id,
  } as Startgroup;
}

function randomInt(min: number, max: number, seed: number): number {
  return Math.floor(min + (max - 1 - min) * Math.abs((Math.sin(1.4241^seed) * 10000) % 1));
} 

const generateParticipants = (count: number): ParticipantDto[] => {
  const participants: ParticipantDto[] = [];

  for (let i = 1; i <= count; i++) {
    const discipline = competitions[0].disciplines[randomInt(0, competitions[0].disciplines.length - 1, i * i + 13)]

    const participant: ParticipantDto = {
      id: i,
      firstname: firstnames[randomInt(0, firstnames.length - 1, i)],
      lastname: lastnames[randomInt(0, lastnames.length - 1, i - 2 * i)],
      startnumber: i + Math.floor(randomInt(0, 101, i)/100) * randomInt(0,44, 5 * i),// Ensures mostly unique start numbers starting from 496
      disciplineId: discipline.id,
      startgroupId: discipline.startgroups[0].id,
      birthYear: randomInt(1960, 2005, 3 * i * i),
      clubOrTeam: clubs[randomInt(0, clubs.length - 1, 4 * i)],
      distanceInMeters: 6300,
      gender: generateGender(discipline.name, i)
    };
    participants.push(participant);
  }

  return participants;
};


function generateGender(klass: string, intNr: number) {
  if (klass === "M" || klass.startsWith("P")) {
    return Gender.MALE;
  }
  else if (klass === "K" || klass.startsWith("F")) {
    return Gender.FEMALE;
  }
  return intNr % 2 == 1 ? Gender.MALE : Gender.FEMALE;
}


function generateResults(competitionTag: string, count: number): ResultDto[] {
  const results: ResultDto[] = [];

  for (let i = 1; i <= count; i++) {
    const c = classes[randomInt(0, classes.length - 1, i)];

    const result: ResultDto = {
      id: i.toString(),
      firstname: firstnames[randomInt(0, firstnames.length - 1, i)],
      lastname: lastnames[randomInt(0, lastnames.length - 1, i - 2 * i)],
      birthYear: randomInt(1960, 2005, 3 * i * i),
      disciplineName: c,
      clubOrTeam: clubs[randomInt(0, clubs.length - 1, 4 * i)],
      startnumber: i + Math.floor(randomInt(0, 101, i)/100) * randomInt(0,44, 5 * i),
      distanceInMeters: 6300,
      startTime: 1742850370708,
      finishTime: 1742850370708 + randomInt(1000 * 60 * 20, 1000 * 60 * 40, i + 4 * i),
      startgroupId: competitions.filter(c => c.tag === competitionTag)[0].disciplines.filter((d) => d.name === c)[0].startgroups[0].id,
      gender: generateGender(c, i),
    };
    results.push(result);
  }

  return results;
};
